import { gql, useQuery } from '@apollo/client';
import { ClientGroupTypes, type ObjectTypes } from '@generated/graphql';
import type { ClientGroup } from '@interfaces/clientGroup';
import { CompletionBox } from '@ovComponents/3-pattern/completionBox/completionBox';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { Typography, useTheme } from 'hephaestus/web';
import { first } from 'lodash-es';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkflowActions } from './householdSelection.constants';
import { HouseholdSelectionModal } from './householdSelectionModal';

const FETCH_USER = gql`
  query fetchUserType($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        households {
          id: stringId
          type
          name
        }
      }
    }
  }
`;

export const HouseholdSelection = ({
  updateMode = false,
  editable = true,
  refetch,
  data,
  updateData,
  objectType,
  infoTooltip,
}: {
  refetch?: () => void;
  updateMode?: boolean;
  editable?: boolean;
  data?: any;
  updateData?: any;
  objectType: ObjectTypes;
  infoTooltip?: string;
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [action, setAction] = useState<WorkflowActions>(WorkflowActions.CREATE);
  const { workflowData, setWorkflowData, userId } = useContext(WorkflowContext);
  const currentUserId = workflowData.currentUserId || userId;
  const { t } = useTranslation(['affiliationTypes', 'client']);
  const theme = useTheme();

  const { data: userData } = useQuery(FETCH_USER, {
    variables: {
      userId: currentUserId,
    },
    fetchPolicy: 'no-cache',
    skip: !currentUserId,
  });
  let households: Partial<ClientGroup>[] = [];
  if (userData?.fetchUser.user?.households) {
    households = userData?.fetchUser.user?.households.filter(
      (a: any) => a.type === ClientGroupTypes.Household,
    );
  }

  const hasHouseholdClientGroupAssigned =
    data?.householdClientGroupId !== undefined &&
    data?.householdClientGroupId !== null &&
    data?.householdClientGroupId !== '';

  let householdName = '';
  if (hasHouseholdClientGroupAssigned) {
    const currentHouseholds = households.filter(
      (a: any) => a.id === data?.householdClientGroupId,
    );
    if (currentHouseholds && currentHouseholds.length > 0) {
      const selectedHouseholdName = first(currentHouseholds)?.name ?? '';
      householdName = selectedHouseholdName;
    }
  } else if (
    households &&
    households.length === 1 &&
    !workflowData.isHouseholdRemoved
  ) {
    updateData({ ...data, householdClientGroupId: households[0].id });
    setWorkflowData({
      ...workflowData,
      currentHouseholdClientGroupId: households[0].id,
    });
  }

  return (
    <>
      <CompletionBox
        testId='household-completion'
        variant={updateMode ? 'view' : 'edit'}
        state={hasHouseholdClientGroupAssigned ? 'complete' : 'todo'}
        edittable={editable}
        infoTooltip={infoTooltip}
        title={t('householdSelection:title')}
        onEdit={() => {
          setAction(WorkflowActions.EDIT);
          setOpen(true);
        }}
        onRemove={() => {
          setWorkflowData({
            ...workflowData,
            currentHouseholdClientGroupId: undefined,
            isHouseholdRemoved: true,
          });
          updateData({ ...data, householdClientGroupId: null });
        }}
        onAdd={() => {
          setAction(WorkflowActions.CREATE);
          setOpen(true);
        }}
      >
        {hasHouseholdClientGroupAssigned ? (
          <>
            <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
              <span style={{ color: theme.color.onSurfaceVariant }}>
                {t('householdSelection:name')}:
              </span>
              <b>{householdName ?? ''}</b>
            </Typography>
          </>
        ) : (
          <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
            {t('householdSelection:body', {
              objectType: objectType.toLocaleLowerCase(),
            })}
          </Typography>
        )}
      </CompletionBox>
      <HouseholdSelectionModal
        data-testid='household-selection'
        open={open}
        setOpen={setOpen}
        refetch={refetch}
        action={action}
        relatedHouseholds={households}
        data={data}
        updateData={updateData}
      />
    </>
  );
};
